import { useState } from "react";
import { toast } from "sonner";
import { ChatText, EnvelopeSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../api";
import { FadeIn } from "../FadeIn";

const INPUT_CLS =
  "w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-none transition-colors";

const CHANNELS = [
  { label: "Text", value: "606-471-4369", href: "sms:+16064714369", icon: ChatText, testid: "contact-text-link" },
  { label: "Email", value: "Appguns@yahoo.com", href: "mailto:Appguns@yahoo.com", icon: EnvelopeSimple, testid: "contact-email-link" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/messages", form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", contact: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer id="contact" data-testid="contact-section" className="border-t border-white/10">
      <div className="px-6 md:px-12 pt-24 md:pt-32">
        <FadeIn>
          <h2 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] md:text-[10vw] text-outline hover:text-violet-300 hover:[-webkit-text-stroke:0px] transition-all duration-700 select-none">
            Let's Talk
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-8 mt-16">
          {CHANNELS.map((c, i) => (
            <FadeIn key={c.label} delay={i * 0.08}>
              <a
                href={c.href}
                data-testid={c.testid}
                className="group block border border-white/10 p-8 hover:border-primary transition-colors"
              >
                <c.icon size={28} weight="duotone" className="text-primary" />
                <p className="text-xs uppercase tracking-[0.25em] text-white/40 mt-6">{c.label}</p>
                <p className="text-lg md:text-xl mt-2 group-hover:text-primary transition-colors">{c.value}</p>
              </a>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.2}>
          <form data-testid="contact-form" onSubmit={submit} className="mt-16 border border-white/10 p-8 md:p-12 grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="msg-name">Name</label>
              <input id="msg-name" data-testid="message-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="msg-contact">Email or Phone</label>
              <input id="msg-contact" data-testid="message-contact-input" required value={form.contact} onChange={set("contact")} placeholder="Where do we reply?" className={INPUT_CLS} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="msg-body">Message</label>
              <textarea id="msg-body" data-testid="message-body-input" required rows={4} value={form.message} onChange={set("message")} placeholder="What's on your mind?" className={INPUT_CLS} />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                data-testid="message-submit-btn"
                disabled={busy}
                className="bg-primary text-white px-10 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors disabled:opacity-50"
              >
                {busy ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </FadeIn>
      </div>

      <div className="mt-24 border-t border-white/10 px-6 md:px-12 py-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs uppercase tracking-[0.25em] text-white/30">
        <p data-testid="footer-copy" className="flex items-center gap-4">
          <img src="/assets/logo.png" alt="Appalachian Guns LLC logo" data-testid="footer-logo" className="h-12 w-auto" />
          © 2026 Appalachian Guns LLC · Licensed FFL/SOT · Johnson County, KY
        </p>
        <p data-testid="footer-tagline">Built in the foothills. Run on trust.</p>
      </div>
    </footer>
  );
};
